import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { firstValueFrom, shareReplay, interval, switchMap, distinctUntilChanged, startWith, Observable } from 'rxjs'
import { SupportedLanguage } from '../utils/language'
import { isEqual } from 'lodash-es'

export type AppConfig = {
  deployedAt:string,
  auth: {
    cognitoUserPoolId: string,
    cognitoClientId:string
  },
  auth2: {
    cognitoUserPoolId: string,
    cognitoClientId:string,
    enableNewAuthFrontend?: boolean
  },
  domain: {
    name: string,
    types: Record<string, {
      traits: {
        chartsSeriesDisplay?: {
          template: 'supplier-meter' | 'single-property'
        }
      }
    }>
  },
  i18n: {
    availableLanguages: string[]
  },
  map: {
    mapboxAccessToken: string,
    mapboxStyleUrl: string
  },
  charts: {
    categories: {
      label: string,
      filter: string,
      groupBy: string[]
    }[],
    defaultCategory?:string,
    enableCustomization: boolean,
    enablePeriodBasedTimeNavigationFor?: string[],
    colorPalette?: string[]
  },
  readings?: {
    enableForAll?: boolean
  },
  reports?: {
    enable?: boolean,
    enableForSpecificUsers?: string[]
  },
  waste?: {
    enableForAll?: boolean,
    enableForSpecificUsers?: string[],
    enableTimeIntervalCollectionType?: boolean,
    enableFoodWasteCommitment?: boolean,
    foodWasteIntensityFactorUnit?: string,
    totalIntensityFactorUnit?: string,
    manualFoodWasteBaselineLink?: string,
    defaultContainerFillFactors?: DefaultContainerFillFactors,
    infoTooltips?: Record<string, Record<string, string>>
  },
  watches?: {
    enableForAll?: boolean,
    enableForSpecificUsers?: string[],
  },
  remoteAccess?: {
    enableForAll?: boolean,
    enableForSpecificUsers?: string[],
  },
  exports?: {
    enableAllForSpecificUsers?: string[],
    exports?: {
      name: {default: string, translations: Record<SupportedLanguage, string>},
      enableForSpecificUsers: string[],
      href: string
    }[]
  },
  resources?: {
    scopeSpecific?: Record<string, {name:string, url:string}[]>
  },
  theme?: {
    logo?: {
      url:string
    }
  },
  helpUrl?: string,
  domainNameTransitions?: Record<string, string>
}

export type DefaultContainerFillFactors = {
  hazardous?: {
    battery?: number
    bulb?: number
    paint?: number
    chemical?: number
    electronicWaste?: number
    other?: number
  }
  recyclable?: {
    all?: number
    paper?: number
    cardboard?: number
    plastic?: number
    glass?: number
    metal?: number
    pallet?: number
    furniture?: number
    textile?: number
  }
  organic?: {
    greenWaste?: number
    solidFood?: number
    kitchenFat?: number
  }
  construction?: {
    wood?: number
    stone?: number
    plaster?: number
    demolition?: number
  }
  residual?: {
    residual?: number
  }
}

export type HelpTexts = {
  contractorAndOtherDisposalMethods?: {
    contractorAndOther?: string
  },
  monthlyReporting?: {
    mandatoryOnly?: string,
    collected?: string,
    disposalMethod?: string,
    collectionType?: string,
    destination?: string,
    allRecyclable?: string,
    greenWaste?: string,
    bioWaste?: string,
    kitchenFat?: string,
    residual?: string
  },
  homeScreen?: {
    baselineIndicator?: string
  }
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  public state$: Observable<AppConfig> = interval(60 * 1000).pipe(
    startWith(0),
    switchMap(()=> this.http.get<AppConfig>('./assets/appconfig.json')),
    distinctUntilChanged(isEqual),
    shareReplay(1),
  )
  constructor (
    private http: HttpClient
  ) { }
  public async init ():Promise<void> {
    await firstValueFrom(this.state$)
  }
}
